/**
 * M7 S40: the derelict survey card as the player sees it (plan S40; spec §16.3 step 1, economy plan M7-7).
 *
 * - **The targeted scan:** a deep-scan ping while the nav target is a discovered derelict POI within the scanner's
 *   radius (`exploration/scanner.ts surveyTargetPoi`) with Scanner **Mk II+** (`reactivation.json
 *   survey.minScannerTier`) reads the survey card (S15 `surveyCard`) and records it (`recordSurvey`: a `surveyed`
 *   record for a restorable station; the Harrow sets `harrow_surveyed`).
 * - **The card's text UI** (`surveyCardModel`): the title, restorable or not, hazards and their state, squatters and
 *   the fence fee, a Holdfast marker's family, the sealed core, bunker tetherstone, the reboot defence, and the kit
 *   table with **delivered / needed** per row and SY-1's **condition-band column** (kit components must be
 *   Serviceable or better; the hold's best band is shown beside it). Copy in `text.json`.
 * - **The Rush-pattern bonus line** (M8 MC-h4b, U14): under the kit table, what one Rush-pattern part saves in fuel
 *   cells (`reactivation.json rushBonus`); shown only while the kit still needs fuel cells. Never a requirement.
 *
 * Pure (Career in, result out); survey time only, never per frame.
 */
import { z } from 'zod'
import { GOOD_NAMES } from '../../career/ops'
import { goodT, type GoodId } from '../../career/parts'
import type { Career } from '../../career/types'
import { COMPONENT_TYPES, type ComponentType } from '../../data/m2Schemas'
import type { Galaxy } from '../../galaxy/types'
import type { RelinkConfig } from '../../relink/config'
import { conditionBand, CONDITION_BAND, type ConditionBand } from '../../salvage/generate'
import { m3Complete, type StorySites } from '../../story/reactivation/eligibility'
import { SALVAGE_BANDS, kitMissing, recordRequirement, kitRequirement, type KitRequirement, type SalvageBands } from '../../story/reactivation/kit'
import { canSurvey, recordSurvey, surveyCard, type SurveyCard } from '../../story/reactivation/survey'
import type { ReactivationConfig } from '../../story/schemas'
import { storyOf } from '../../story/state'
import type { StoryReactivation, StoryResult } from '../../story/types'
import { fillTokens } from '../world'
import textJson from './text.json'

// ---------------------------------------------------------------------------------------------
// text.json
// ---------------------------------------------------------------------------------------------

const line = z.string().min(1)
/** Every key required (zod 4: a record over an enum is exhaustive). */
const record = <const K extends readonly [string, ...string[]]>(keys: K) => z.record(z.enum(keys), line)

export const reactivationTextSchema = z.strictObject({
  comment: z.string(),
  card: record([
    'title',
    'surveyTitle',
    'close',
    'restorable',
    'notRestorable',
    'kitHeader',
    'kitRow',
    'goodRow',
    'refit',
    'coolant',
    'coolantVented',
    'wreckers',
    'family',
    'squatCleared',
    'marker',
    'sealedCore',
    'bunker',
    'reboot',
    'guardians',
    'rushBonus',
  ]),
  classes: record(['habRing', 'oreProcessor', 'depot', 'lab']),
  components: record(COMPONENT_TYPES),
  bands: record(['intact', 'serviceable', 'wrecked', 'required']),
  prompts: record(['vent', 'fence', 'hail', 'deliver', 'restart', 'fight']),
  refusals: record([
    'noRecord',
    'closed',
    'harrow',
    'range',
    'speed',
    'hazard',
    'squatters',
    'family',
    'funds',
    'nothing',
    'incomplete',
    'retry',
    'busy',
    'scanner',
  ]),
  messages: record(['vented', 'ventedHot', 'paid', 'fought', 'family', 'familyHail', 'delivered', 'kitComplete', 'online', 'scram', 'aborted', 'restartStarted', 'squatOut', 'squatLeft', 'rushCredit']),
})
export type ReactivationText = z.output<typeof reactivationTextSchema>

let cachedText: ReactivationText | undefined

/** The shipped `text.json`, parsed once. */
export function reactivationText(): ReactivationText {
  cachedText ??= reactivationTextSchema.parse(textJson)
  return cachedText
}

// ---------------------------------------------------------------------------------------------
// The targeted survey
// ---------------------------------------------------------------------------------------------

export interface SurveyInput {
  galaxy: Galaxy
  systemId: string
  poiId: string
  /** The fitted scanner's Mk. */
  scannerTier: number
  career: Career
  /** The story sites the exclusions read (the Harrow, Column Nine, Okafor's lab). */
  sites: StorySites
  cfg: ReactivationConfig
  relink: RelinkConfig
}

export type SurveyRefusal = 'scanner' | 'unknownPoi' | 'notDerelict'

export type SurveyOutcome = { ok: false; reason: SurveyRefusal } | { ok: true; card: SurveyCard; result: StoryResult<Career> }

/**
 * A targeted scan of a derelict POI: refused below Mk II, for an unknown POI or one that is not a derelict; else the
 * card and the recorded survey (re-surveying records nothing new).
 */
export function targetedSurvey(input: SurveyInput): SurveyOutcome {
  if (!canSurvey(input.scannerTier, input.cfg)) return { ok: false, reason: 'scanner' }
  const system = input.galaxy.systems.find((s) => s.id === input.systemId)
  const poi = system?.pois.find((p) => p.id === input.poiId)
  if (system === undefined || poi === undefined) return { ok: false, reason: 'unknownPoi' }
  if (poi.kind !== 'derelict') return { ok: false, reason: 'notDerelict' }
  const story = storyOf(input.career)
  const card = surveyCard(input.galaxy, system, poi, { sites: input.sites, m3Complete: m3Complete(story) }, input.cfg, input.relink)
  return { ok: true, card, result: recordSurvey(input.career, card, story.onlineDays) }
}

// ---------------------------------------------------------------------------------------------
// The card model (text UI)
// ---------------------------------------------------------------------------------------------

export type BandKey = 'intact' | 'serviceable' | 'wrecked'

const BAND_KEYS: Readonly<Record<ConditionBand, BandKey>> = {
  [CONDITION_BAND.intact]: 'intact',
  [CONDITION_BAND.serviceable]: 'serviceable',
  [CONDITION_BAND.wrecked]: 'wrecked',
}

/** One row of the kit table. */
export interface SurveyKitRow {
  kind: 'component' | 'good'
  id: ComponentType | GoodId
  label: string
  need: number
  delivered: number
  /** Components: kit-ready units in the hold (Serviceable or better); goods: whole tonnes in the hold. */
  inHold: number
  /** SY-1's condition-band column: the band a component must reach (null for goods). */
  band: string | null
  /** The best band of that component type in the hold (null for goods or none in the hold). */
  holdBest: BandKey | null
}

export interface SurveyCardModel {
  poiId: string
  title: string
  restorable: boolean
  /** The card's lines in order (the text UI), the kit table included. */
  lines: string[]
  kit: SurveyKitRow[]
}

const pct = (x: number): string => String(Math.round(x * 100))

/** The best condition band of `type` in the hold, or null when there is none. */
export function holdBestBand(career: Career, type: ComponentType, bands: SalvageBands = SALVAGE_BANDS): BandKey | null {
  let best: ConditionBand | null = null
  for (const c of career.cargo.components) {
    if (c.type !== type) continue
    const b = conditionBand(c.condition, bands)
    if (best === null || b < best) best = b
  }
  return best === null ? null : BAND_KEYS[best]
}

/** Kit-ready units of `type` in the hold (Serviceable or better). */
export function holdReady(career: Career, type: ComponentType, bands: SalvageBands = SALVAGE_BANDS): number {
  let n = 0
  for (const c of career.cargo.components) if (c.type === type && conditionBand(c.condition, bands) !== CONDITION_BAND.wrecked) n++
  return n
}

/** The kit table: what the restart needs, what is delivered (the record's progress) and what the hold carries. */
export function kitRows(
  req: KitRequirement,
  record: StoryReactivation | undefined,
  career: Career,
  text: ReactivationText = reactivationText(),
  bands: SalvageBands = SALVAGE_BANDS,
): SurveyKitRow[] {
  const rows: SurveyKitRow[] = []
  for (const t of COMPONENT_TYPES) {
    const need = req.components[t] ?? 0
    if (need <= 0) continue
    rows.push({
      kind: 'component',
      id: t,
      label: text.components[t],
      need,
      delivered: record?.kit?.components[t] ?? 0,
      inHold: holdReady(career, t, bands),
      band: text.bands.required,
      holdBest: holdBestBand(career, t, bands),
    })
  }
  for (const [g, need] of Object.entries(req.goods) as [GoodId, number | undefined][]) {
    if (need === undefined || need <= 0) continue
    rows.push({
      kind: 'good',
      id: g,
      label: GOOD_NAMES[g],
      need,
      delivered: record?.kit?.goods[g] ?? 0,
      inHold: Math.floor(goodT(career.cargo.parts, g) + 1e-9),
      band: null,
      holdBest: null,
    })
  }
  return rows
}

function rowLine(r: SurveyKitRow, text: ReactivationText): string {
  const vars = { label: r.label, delivered: String(r.delivered), need: String(r.need), inHold: String(r.inHold), band: r.band ?? '' }
  if (r.kind === 'good') return fillTokens(text.card.goodRow, vars)
  const best = r.holdBest === null ? '' : ` [${text.bands[r.holdBest]}]`
  return `${fillTokens(text.card.kitRow, vars)}${best}`
}

/**
 * The survey card's text UI for `card`, with the kit progress of the Career's record (if any) and the hold's
 * contents. The Harrow's card is its scan reading and the in-fiction refusal.
 */
export function surveyCardModel(
  card: SurveyCard,
  career: Career,
  cfg: ReactivationConfig,
  text: ReactivationText = reactivationText(),
  bands: SalvageBands = SALVAGE_BANDS,
): SurveyCardModel {
  if (card.kind === 'harrow') {
    return {
      poiId: card.poiId,
      title: card.designation,
      restorable: false,
      lines: [card.text ?? '', card.refusal ?? text.refusals.harrow],
      kit: [],
    }
  }
  const cls = card.frClass
  const title = fillTokens(text.card.title, { designation: card.designation, class: cls === undefined ? '' : text.classes[cls] })
  const lines: string[] = [card.restorable ? text.card.restorable : text.card.notRestorable]
  if (!card.restorable || cls === undefined) return { poiId: card.poiId, title, restorable: false, lines, kit: [] }
  const record = storyOf(career).reactivations.find((r) => r.poiId === card.poiId)
  if (card.coolantHazard) lines.push(record?.vented === true ? text.card.coolantVented : text.card.coolant)
  if (record?.cleared !== undefined && card.squatters !== 'none') lines.push(text.card.squatCleared)
  else if (card.squatters === 'wreckers') lines.push(fillTokens(text.card.wreckers, { fee: String(card.fenceFee) }))
  else if (card.squatters === 'holdfastFamily') lines.push(fillTokens(text.card.family, { family: card.markerFamily ?? '' }))
  if (card.markerFamily !== null) lines.push(fillTokens(text.card.marker, { family: card.markerFamily }))
  if (card.sealedCore) lines.push(text.card.sealedCore)
  if (card.bunkerT > 0) lines.push(fillTokens(text.card.bunker, { tonnes: String(card.bunkerT) }))
  if (card.reboot !== undefined) {
    lines.push(
      fillTokens(text.card.reboot, {
        seconds: String(card.reboot.seconds),
        waves: String(card.reboot.schedule.length),
        guardians: card.reboot.guardians ? text.card.guardians : '',
      }),
    )
  }
  const req = (record === undefined ? null : recordRequirement(record, cfg)) ?? card.kit ?? kitRequirement(cls, cfg)
  const kit = kitRows(req, record, career, text, bands)
  lines.push(text.card.kitHeader)
  for (const r of kit) lines.push(rowLine(r, text))
  if (cfg.rushBonus.fuelCellsPerPart > 0 && (kitMissing(req, record?.kit).goods.fuel_cells ?? 0) > 0) {
    lines.push(fillTokens(text.card.rushBonus, { cells: String(cfg.rushBonus.fuelCellsPerPart) }))
  }
  if (card.refit !== undefined) lines.push(fillTokens(text.card.refit, { template: card.refit.template, costPct: pct(card.refit.costMult) }))
  return { poiId: card.poiId, title, restorable: true, lines, kit }
}
